/**
 * The Query Tool's markdown subset (chat-ui-spec §6.1, contract §8.1), producing a small tree that
 * the answer component renders as React elements. Nothing here is ever treated as HTML: text stays
 * text, so model output cannot inject markup (the original escapes first for the same reason).
 *
 * Blocks: fenced code (an unclosed fence still renders), GFM tables with alignment and ragged rows
 * padded or trimmed to the header, `#`–`###` headings, flat `-`/`*`/`+` and `1.`/`1)` lists (a
 * numbered list starts at the number its first item has, so "3." after a bullet list is still 3),
 * paragraphs (lines joined with a space). Inline, in this order: code spans, citation groups
 * (`[n]`, `[n, m]`, only when every number is known), http(s) links, `**bold**`, `*italic*`.
 */

export type Align = 'center' | 'right' | null;

export type MdInline =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'strong'; children: MdInline[] }
  | { type: 'em'; children: MdInline[] }
  | { type: 'link'; href: string; children: MdInline[] }
  | { type: 'cite'; numbers: number[] };

export type MdBlock =
  | { type: 'paragraph'; children: MdInline[] }
  | { type: 'heading'; level: 1 | 2 | 3; children: MdInline[] }
  | { type: 'list'; ordered: boolean; start?: number; items: MdInline[][] }
  | { type: 'code'; text: string }
  | { type: 'table'; align: Align[]; header: MdInline[][]; rows: MdInline[][][] };

/** Citation number → link (a string) or the details card (null). Absent numbers stay text. */
export type CitationTargets = ReadonlyMap<number, string | null>;

const OPEN = '';
const CLOSE = '';
const PLACEHOLDER_RE = /(\d+)/;
const CODE_RE = /`([^`]+)`/g;
const CITE_RE = /\[(\d+(?:\s*,\s*\d+)*)\]/g;
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const BOLD_RE = /\*\*(.+?)\*\*/g;
const ITALIC_RE = /\*([^*\s](?:[^*]*?[^*\s])?)\*/g;

type Parked =
  | { kind: 'code'; text: string }
  | { kind: 'cite'; numbers: number[] }
  | { kind: 'link'; href: string; raw: string }
  | { kind: 'strong'; raw: string }
  | { kind: 'em'; raw: string };

export function parseInline(source: string, links?: CitationTargets): MdInline[] {
  const parked: Parked[] = [];
  const park = (item: Parked) => `${OPEN}${parked.push(item) - 1}${CLOSE}`;
  const bold = (s: string) => s.replace(BOLD_RE, (_, raw: string) => park({ kind: 'strong', raw }));
  const italic = (s: string) => s.replace(ITALIC_RE, (_, raw: string) => park({ kind: 'em', raw }));

  let s = source.replace(/[]/g, '');
  s = s.replace(CODE_RE, (_, text: string) => park({ kind: 'code', text }));
  if (links) {
    s = s.replace(CITE_RE, (match, group: string) => {
      const numbers = group.split(',').map((n) => Number(n.trim()));
      return numbers.every((n) => links.has(n)) ? park({ kind: 'cite', numbers }) : match;
    });
  }
  s = s.replace(LINK_RE, (_, raw: string, href: string) => park({ kind: 'link', href, raw }));
  s = italic(bold(s));

  const build = (text: string): MdInline[] => {
    const out: MdInline[] = [];
    const pushText = (t: string) => {
      if (!t) return;
      const last = out[out.length - 1];
      if (last && last.type === 'text') last.text += t;
      else out.push({ type: 'text', text: t });
    };
    const parts = text.split(PLACEHOLDER_RE);
    parts.forEach((part, index) => {
      if (index % 2 === 0) {
        pushText(part);
        return;
      }
      const item = parked[Number(part)];
      if (!item) return;
      switch (item.kind) {
        case 'code':
          out.push({ type: 'code', text: item.text });
          break;
        case 'cite':
          out.push({ type: 'cite', numbers: item.numbers });
          break;
        // Bold and italic run over the whole line after links, so a link's text can still be
        // bold or italic, and a bold run can still hold italics.
        case 'link':
          out.push({ type: 'link', href: item.href, children: build(italic(bold(item.raw))) });
          break;
        case 'strong':
          out.push({ type: 'strong', children: build(italic(item.raw)) });
          break;
        case 'em':
          out.push({ type: 'em', children: build(item.raw) });
          break;
        default:
          break;
      }
    });
    return out;
  };

  return build(s);
}

const splitRow = (line: string): string[] => {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map((cell) => cell.trim());
};

const isDelimiterRow = (line: string | undefined): boolean => {
  if (line === undefined || !line.includes('-')) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));
};

const alignOf = (cell: string): Align => {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  return null;
};

const fitRow = <T>(cells: T[], width: number, filler: () => T): T[] =>
  cells.length >= width
    ? cells.slice(0, width)
    : [...cells, ...Array.from({ length: width - cells.length }, filler)];

export function parseMarkdown(source: string, links?: CitationTargets): MdBlock[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MdBlock[] = [];
  let paragraph: string[] | null = null;
  let list: { ordered: boolean; start?: number; items: string[] } | null = null;
  let fence: string[] | null = null;

  const closeParagraph = () => {
    if (paragraph)
      blocks.push({ type: 'paragraph', children: parseInline(paragraph.join(' '), links) });
    paragraph = null;
  };
  const closeList = () => {
    if (list) {
      blocks.push({
        type: 'list',
        ordered: list.ordered,
        ...(list.start !== undefined ? { start: list.start } : {}),
        items: list.items.map((item) => parseInline(item, links)),
      });
    }
    list = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (fence) {
      if (line.trimStart().startsWith('```')) {
        blocks.push({ type: 'code', text: fence.join('\n') });
        fence = null;
      } else {
        fence.push(line);
      }
      continue;
    }
    const t = line.trim();
    if (t.startsWith('```')) {
      // The opening line is dropped, language tag and all: there is no highlighting.
      closeParagraph();
      closeList();
      fence = [];
      continue;
    }
    if (!t) {
      closeParagraph();
      closeList();
      continue;
    }
    if (t.includes('|') && isDelimiterRow(lines[i + 1])) {
      closeParagraph();
      closeList();
      const headerCells = splitRow(t);
      const width = headerCells.length;
      const align = fitRow(splitRow(lines[i + 1]).map(alignOf), width, () => null as Align);
      const rows: MdInline[][][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(fitRow(splitRow(lines[i]), width, () => '').map((c) => parseInline(c, links)));
        i += 1;
      }
      i -= 1;
      blocks.push({
        type: 'table',
        align,
        header: headerCells.map((c) => parseInline(c, links)),
        rows,
      });
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(t);
    if (heading) {
      closeParagraph();
      closeList();
      blocks.push({
        type: 'heading',
        level: heading[1].length as 1 | 2 | 3,
        children: parseInline(heading[2], links),
      });
      continue;
    }
    const bullet = /^[-*+]\s+(.*)$/.exec(t);
    const numbered = bullet ? null : /^(\d+)[.)]\s+(.*)$/.exec(t);
    if (bullet || numbered) {
      closeParagraph();
      const ordered = !!numbered;
      if (list && list.ordered !== ordered) closeList();
      if (!list) {
        list = { ordered, items: [] };
        // The first item's own number, so an answer that puts bullets between its numbered items
        // ("1. …", bullets, "2. …") still reads 1, 2, not 1, 1. A list that starts at 1 needs none.
        const first = numbered ? Number(numbered[1]) : 1;
        if (first !== 1 && Number.isSafeInteger(first)) list.start = first;
      }
      list.items.push(numbered ? numbered[2] : (bullet as RegExpExecArray)[1]);
      continue;
    }
    closeList();
    if (!paragraph) paragraph = [];
    paragraph.push(t);
  }
  if (fence) blocks.push({ type: 'code', text: (fence as string[]).join('\n') });
  closeParagraph();
  closeList();
  return blocks;
}
